import AgendaItem from "@/domain/entities/agenda/AgendaItem";
import useGetUserProfile from "@/hooks/useGetUserProfile";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import EventDetailsBlock, {
    canAnswerInvitation,
    eventTimeRange,
} from "../EventDetailsBlock";
import type { EventTab } from "./sidePanes";

/**
 * An event in its tab: its details (attendees, description, meeting link), accept or decline for
 * an invitation still to come, and the link to the calendar.
 */
const EventPane = ({
    tab,
    onChange,
}: {
    tab: EventTab;
    /** The event answered from the tab: the tab keeps the new state. */
    onChange: (event: AgendaItem) => void;
}) => {
    const { t, i18n } = useTranslation();
    const { data: user } = useGetUserProfile();
    const { event } = tab;
    const start = new Date(event.start);
    const when = [
        start.toLocaleDateString(i18n.language, {
            weekday: "long",
            day: "numeric",
            month: "long",
        }),
        eventTimeRange(
            event,
            i18n.language,
            t("dashboard.widgets.calendar.event.allDay"),
        ),
        event.location?.displayName,
    ]
        .filter(Boolean)
        .join(" · ");
    const canAnswer = canAnswerInvitation(event, user);

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto bg-white px-5 py-4 text-primary-darkest">
            <div className="flex flex-col gap-0.5">
                <h2
                    className={cn(
                        "text-base font-semibold",
                        event.isCancelled && "line-through",
                    )}
                >
                    {event.subject || t("vistassistant.untitledEvent")}
                </h2>
                <p className="text-[13px] text-neutral-dark first-letter:uppercase">
                    {when}
                </p>
                {event.isCancelled && (
                    <p className="text-[13px] font-semibold text-error-darkest">
                        {t("vistassistant.cancelledEvent")}
                    </p>
                )}
            </div>
            <EventDetailsBlock
                canAnswer={canAnswer}
                event={event}
                onAnswered={onChange}
            />
        </div>
    );
};

export default EventPane;
